import React from 'react';
import { useAddExercisePropertyComponent } from './useAddExercisePropertyComponent';
import { Box, Checkbox, FormControlLabel, Grid } from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import AppFormSelectWrapper from '../../../../components/designe/app-form-select-wrapper';
import { useTranslation } from 'react-i18next';
import AppButton from '../../../../components/designe/app-button/AppButton';
import { useStyles } from './AddExerciseProperty.styles';
import theme from '../../../../styles/theme';
import { IExerciseProperty } from '../../../../types/interfaces/exercise-property';
import { ExercisePropertyType } from '../../../../shared/constants';
import AppFormTextField from '../../../../components/designe/app-form-text-field';
import AppEntityAvatar, {
  AvatarSize,
} from '../../../../components/designe/app-entity-avatar/AppEntityAvatar';
import { AvatarTypes } from '../../../../components/designe/app-avatar/AppAvatar';
import { uploadFilesUtils } from '../../../../utils/upload-files-utils';

interface IAddExercisePropertyProps {
  exerciseProperty?: IExerciseProperty;
  onClose: () => void;
}

export default function AddExerciseProperty(props: IAddExercisePropertyProps) {
  const { exerciseProperty, onClose } = props;
  const { t } = useTranslation();
  const { formik, isLoading, avatar, setAvatar, exerciseProperties } =
    useAddExercisePropertyComponent(onClose, exerciseProperty);
  const { classes } = useStyles(theme);

  const exercisePropertyTypeOptions = Object.values(ExercisePropertyType)?.map(
    (option) => ({
      id: option,
      name: option,
    }),
  );

  const parentOptions = exerciseProperties
    ?.filter((o) => o.id !== exerciseProperty?.id)
    .map((o) => ({
      id: o.id,
      name: `${o.name} - ${o.type}`,
    }));

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={1}>
          <Grid item sm={12}>
            <div className={classes.avatarWrapper}>
              <AppEntityAvatar
                fileBlob={avatar?.fileBlob}
                entity={exerciseProperty}
                size={AvatarSize.l}
                type={AvatarTypes.square}
              />
            </div>
            <AppButton
              variant="outlined"
              component="label"
              className={classes.uploadFileButton}>
              <input
                autoComplete="off"
                style={{ display: 'none' }}
                accept="image/png, image/jpeg"
                tabIndex={-1}
                type="file"
                onChange={(e) => {
                  uploadFilesUtils(e.target.files, true).then(
                    (uploadedFiles) => {
                      setAvatar(uploadedFiles[0]);
                    },
                  );
                }}
              />
              <UploadIcon />
              {t('common.avatar')}
            </AppButton>
          </Grid>
          <Grid item sm={12}>
            <AppFormTextField
              label={t('common.nameKey')}
              fullWidth
              name="nameKey"
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormTextField
              label={t('common.priority')}
              fullWidth
              type="number"
              name="priority"
              formik={formik}
            />
          </Grid>
          <Grid item sm={12}>
            <AppFormSelectWrapper
              label={t('common.type')}
              name="type"
              options={exercisePropertyTypeOptions}
              defaultValue={exercisePropertyTypeOptions?.find(
                (t) => t.id === formik.values.type,
              )}
              onChange={(e) => formik.setFieldValue('type', e.target.value.id)}
              formik={formik}></AppFormSelectWrapper>
          </Grid>
          <Grid item sm={12}>
            <AppFormSelectWrapper
              label={t('adminDashboard.parent')}
              name="parent"
              options={parentOptions}
              defaultValue={parentOptions?.find(
                (t) => t.id === formik.values.parent?.ids,
              )}
              formik={formik}></AppFormSelectWrapper>
          </Grid>
          <Grid item sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={formik.values.active}
                  onChange={(e) =>
                    formik.setFieldValue('active', e.target.checked)
                  }></Checkbox>
              }
              label={t('common.active')}
            />
          </Grid>
          <Grid item sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="isDefault"
                  checked={formik.values.isDefault}
                  onChange={(e) =>
                    formik.setFieldValue('isDefault', e.target.checked)
                  }
                />
              }
              label={t('adminDashboard.defaultForNewExercise')}
            />
          </Grid>
        </Grid>
        <Box className={classes.addExercisePropertyButtonWrapper}>
          <AppButton variant="outlined" onClick={() => onClose()}>
            {t('common.cancel')}
          </AppButton>
          <AppButton type="submit" loader={isLoading}>
            {exerciseProperty ? t('common.update') : t('common.create')}
          </AppButton>
        </Box>
      </form>
    </Box>
  );
}
